import BaseController from "./BaseController";
import JSONModel from "sap/ui/model/json/JSONModel";
import MessageToast from "sap/m/MessageToast";

interface AcceptInvitationViewState {
	tokenInvalid: boolean;
}

const SESSION_KEY_TOKEN = "portal.invitation.token";

/**
 * ADR-017 §2 — Portal landing for invite acceptance.
 *
 * URL: `/accept-invitation?token=<uuid>` (KC executeActionsEmail redirects here after
 * UPDATE_PASSWORD + VERIFY_EMAIL flows complete).
 *
 * Flow:
 *   1. Component bootstrap eagerly auths przez OIDC — to znaczy że gdy widok się
 *      renderuje, user JEST już zalogowany. Token z URL jest persistowany w
 *      sessionStorage, żeby App controller (T16) mógł go odczytać przy auto-accept.
 *   2. Manual "Activate" button — wywołuje `/api/v1/users/me/accept-invitation`
 *      bezpośrednio (user już zalogowany przez Component bootstrap). Na success
 *      przekierowuje na dashboard. Na 4xx pokazuje `tokenInvalid` state.
 *
 * @namespace computec.b2b.portal.controller
 */
export default class AcceptInvitation extends BaseController {
	private _token: string | null = null;

	public onInit(): void {
		const viewState: AcceptInvitationViewState = { tokenInvalid: false };
		this.getView().setModel(new JSONModel(viewState), "view");
		this.getRouter().getRoute("acceptInvitation")?.attachPatternMatched(() => { this._onRouteMatched(); });
	}

	private _onRouteMatched(): void {
		// Gdy auto-accept w App.controller już aktywował usera (Pending → Active), ale route
		// jeszcze `#/accept-invitation` (auto-accept i route match odpalają równolegle), nawigujemy
		// do dashboardu. Bez tego user widzi "Token invalid" na widoku mimo że konto zostało
		// poprawnie aktywowane.
		const userModel = this.getOwnerComponent().getModel("user") as JSONModel | undefined;
		const status = userModel?.getProperty("/status") as string | undefined;
		if (status === "Active") {
			this.getRouter().navTo("dashboard", {}, undefined, true);
			return;
		}

		// UI5 hash routing nie eksponuje query params przez `RouteMatchedEvent.arguments` —
		// czytamy bezpośrednio z `window.location.search`. KC redirect URI to
		// `${PortalBaseUrl}/accept-invitation?token=...` (PR1/T13), więc token siedzi
		// w search part PRZED `#/accept-invitation` (lub po, zależnie od konfiguracji
		// IIS/Kestrel — query persistuje niezależnie od fragment).
		const urlParams = new URLSearchParams(window.location.search);
		const tokenFromUrl = urlParams.get("token");

		if (tokenFromUrl) {
			this._token = tokenFromUrl;
			// Persistujemy dla App controllera (T16) — auto-accept fallback po KC redirect
			// bez interakcji usera.
			try {
				window.sessionStorage.setItem(SESSION_KEY_TOKEN, tokenFromUrl);
			} catch {
				/* private mode → manual button path nadal działa, bo `_token` jest w polu */
			}
			(this.getView().getModel("view") as JSONModel).setProperty("/tokenInvalid", false);
			return;
		}

		// Brak tokena w URL — może SPA przekierował tu bez paramu (np. App controller
		// po failed auto-accept). Spróbuj sessionStorage.
		try {
			this._token = window.sessionStorage.getItem(SESSION_KEY_TOKEN);
		} catch {
			this._token = null;
		}

		if (!this._token) {
			(this.getView().getModel("view") as JSONModel).setProperty("/tokenInvalid", true);
		}
	}

	public async onActivate(): Promise<void> {
		if (!this._token) {
			(this.getView().getModel("view") as JSONModel).setProperty("/tokenInvalid", true);
			return;
		}

		const bundle = await this.getResourceBundle();
		try {
			// ADR-017 §1.7: response.redirectUrl = null dla portal usera (zostajemy w portal),
			// = Luna landing URL dla licensed usera (hard-nav out z SPA).
			const resp = await this.getOwnerComponent().getAuthFetch().json<{ userId: string; redirectUrl: string | null }>(
				"/api/v1/users/me/accept-invitation",
				{
					method: "POST",
					body: JSON.stringify({ invitationToken: this._token })
				});
			try {
				window.sessionStorage.removeItem(SESSION_KEY_TOKEN);
			} catch {
				/* ignore */
			}
			this._token = null;
			if (resp.redirectUrl) {
				window.location.assign(resp.redirectUrl);
				return;
			}
			MessageToast.show(bundle.getText("acceptInvitation.success") ?? "Account activated.");
			this.getRouter().navTo("dashboard", {}, undefined, true);
		} catch {
			// 4xx z backendu zwykle = invitation_not_found / expired / sub mismatch.
			// Pokazujemy generyczny error state — szczegóły idą do KC admina lub auditu.
			(this.getView().getModel("view") as JSONModel).setProperty("/tokenInvalid", true);
		}
	}
}
